import { Component, OnChanges, Input, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { ApiService } from '../../../services/api.service';
import { NgClass } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { AnimationCollapse } from '../../../animations';
import { UntypedFormBuilder, UntypedFormGroup, Validators, UntypedFormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Device, BitParaSettings } from '../../../models/device.model';
import { HelperService } from '../../../services/helper.service';
import { Observable, Subject, forkJoin, of } from "rxjs";
import { catchError, map, throttleTime } from "rxjs/operators";
import { LockBeaconSettingsComponent } from './lock-beacon-settings/lock-beacon-settings.component';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MAT_DATE_FORMATS, MatOptionModule } from '@angular/material/core';
import { dateToHhmm, hhmmToDate, TIMEPICKER_24H_FORMATS } from '../../../shared/timepicker.shared';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatExpansionModule } from '@angular/material/expansion';

@Component({
    selector: 'device-settings',
    templateUrl: './device-settings.component.html',
    styleUrls: ['./device-settings.component.css'],
    animations: [
        AnimationCollapse
    ],
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [FormsModule, ReactiveFormsModule, MatExpansionModule, MatSlideToggleModule, MatFormFieldModule, MatSelectModule, MatOptionModule, MatTooltipModule, NgClass, MatTimepickerModule, LockBeaconSettingsComponent, TranslateModule],
    providers: [{ provide: MAT_DATE_FORMATS, useValue: TIMEPICKER_24H_FORMATS }]
})
export class DeviceSettingsComponent implements OnChanges, OnInit, OnDestroy {

  // Init global variables and services
  constructor(
    private api: ApiService,
    private helper: HelperService,
    private fb: UntypedFormBuilder
  ) { };


  @Input() device: Device;

  deviceSettingsForm: UntypedFormGroup;
  saveSuccess: boolean;
  saveError: boolean;
  /** Detail behind saveError, when the server explained what went wrong. */
  saveErrorMessage: string;
  user: any;

  formSubmitSubject = new Subject<void>();

  createDeviceSettingsForm(): void {
    let iAmOkEnabled: boolean;
    if (this.device.iamOk) {
      iAmOkEnabled = (this.device.iamOk.start !== "00:00" || this.device.iamOk.end !== "00:00");
    }
    this.deviceSettingsForm = this.fb.group({
      devBitParaForm: this.fb.group({
        led: this.device.bitParaSettings?.led,
        alarmBeep: this.device.bitParaSettings?.alarmBeep,
        presenceMsg: this.device.bitParaSettings?.presenceMsg,
        clock: this.device.bitParaSettings?.clock,
        autoAnswer: this.device.bitParaSettings?.autoAnswer,
        ledPulseAlert: this.device.bitParaSettings?.ledPulseAlert,
        qmsgEnabled: this.device.bitParaSettings?.qmsgEnabled,
        ringerVolume: this.device.bitParaSettings?.hasOwnProperty('ringerVolume') ? this.device.bitParaSettings.ringerVolume.toString() : "",
        battLedDisabled: this.device.bitParaSettings?.battLedDisabled,
        homeCare: this.device.bitParaSettings?.homeCare,
        clockBrightness: this.device.bitParaSettings?.hasOwnProperty('clockBrightness') ? (this.device.bitParaSettings?.clockAls ? "4" : this.device.bitParaSettings?.clockBrightness.toString()) : "",
        roamingDisabled: this.device.bitParaSettings?.roamingDisabled,
        voipEnabled: this.device.sipNumber ? true : false,
      }),
      devMiscSettingsForm: this.fb.group({
        limit: [this.helper.convertSecondsToHHMMSS(this.device?.movementTimeLimit).slice(0, -3), Validators.compose([Validators.required, Validators.pattern(new RegExp('^([0-9]{2}):([1-5]{1}[0-9]{1})|([0-9]{1}[1-9]{1}):([0-9]{2})|([1-9]{1}[0-9]{1}):([0-9]{2})$'))])],
        connTimeLimit: [this.helper.convertSecondsToHHMMSS(this.device?.connectionTimeLimit).slice(0, -3), Validators.compose([Validators.required, Validators.pattern(new RegExp('([0-9]{2}):([1-5]{1}[0-9]{1}|00)$'))])],
        activityMonitoring: this.device?.activityMonitoring
      }),
      terminalParameterFormGroup: this.fb.group({
        lockEscort: this.device.terminalParameters?.lockEscort,
        fallingDetection: !this.device.terminalParameters?.lockEscort ? this.device.terminalParameters?.fallingDetection : false,
        movementMonitoring: !this.device.terminalParameters?.lockEscort ? this.device.terminalParameters?.movementMonitoring : false,
        accessControl: !this.device.terminalParameters?.lockEscort ? this.device.terminalParameters?.accessControl : false,
        longPress: this.device.terminalParameters?.longPress,
        alwaysRoaming: this.device.terminalParameters?.alwaysRoaming
      }),
      tp202AdditionalForm: this.fb.group({
        ignoreAutoAnswer: this.device.ignoreAutoAnswerNumbers ? this.fb.array(this.device.ignoreAutoAnswerNumbers) : "",
        dtmfCode: this.device.audioLockOpenDtmfString,
        delayACPowerAlert: this.device.delayACPowerAlert,
        maxCallDuration: this.device.maxCallDuration,
        maxCallDurationExtended: this.device.maxCallDurationExtended,
        iAmOkEnabled: iAmOkEnabled,
        iAmOkStartTime: iAmOkEnabled ? hhmmToDate(this.device.iamOk.start) : null,
        iAmOkEndTime: iAmOkEnabled ? hhmmToDate(this.device.iamOk.end) : null,
      }),
      devNetworkSettingsForm: this.fb.group({
        transportPriority1: [{ value: this.device.transportOrder?.priority1.toString(), disabled: false }, [Validators.required]],
        transportPriority2: [{ value: this.device.transportOrder?.priority2.toString(), disabled: false }, [Validators.required]],
        transportPriority3: [{ value: this.device.transportOrder?.priority3.toString(), disabled: false }, [Validators.required]],
        wifiSSID: this.device.wifiSettings?.ssid,
        wifiPassword: this.device.wifiSettings?.password,
        wifiDHCP: this.device.wifiSettings?.dhcp,
        wifiIPAddress: this.device.wifiSettings?.srcIP,
        wifiGateway: this.device.wifiSettings?.gwAddr,
        wifiSubnetMask: this.device.wifiSettings?.subnetMask,
        ethernetDHCP: this.device.ethernetSettings?.dhcp,
        ethernetIPAddress: this.device.ethernetSettings?.srcIP,
        ethernetGateway: this.device.ethernetSettings?.gwAddr,
        ethernetSubnetMask: this.device.ethernetSettings?.subnetMask,
        hotspotEnabled: this.device.wifiHotSpotSettings?.enabled,
        hotspotPassword: [this.device.wifiHotSpotSettings?.password, Validators.compose([Validators.required, Validators.minLength(8)])],
        hotspotChannel: [this.device.wifiHotSpotSettings?.channel.toString(), Validators.required],
        hotspotIpAddress: [this.device.wifiHotSpotSettings?.ipAddress, Validators.required],
        hotspotGateway: [this.device.wifiHotSpotSettings?.gateway, Validators.required],
        hotspotSubnetMask: [this.device.wifiHotSpotSettings?.subnetMask, Validators.required],
        hotspotDHCPServer: [this.device.wifiHotSpotSettings?.dhcpServerEnabled, Validators.required],
        hotspotDHCPStart: [this.device.wifiHotSpotSettings?.dhcpIpRangeStart, Validators.required],
        hotspotDHCPEnd: [this.device.wifiHotSpotSettings?.dhcpIpRangeEnd, Validators.required],
        simMode: this.device.modemSettings ? this.getCurrentSimMode(this.device.modemSettings?.eSimNetworkMode, this.device.modemSettings?.simMode) : "",
        modemAntMode: this.device.modemSettings?.modemAntMode.toString() === "0" ? "1" : this.device.modemSettings?.modemAntMode.toString(),
        simNetworkMode: typeof this.device.modemSettings?.simNetworkMode !== "undefined" ? (this.device.modemSettings?.simNetworkMode.toString() === "0" ? "5" : this.device.modemSettings?.simNetworkMode.toString()) : null,
        eSimNetworkMode: typeof this.device.modemSettings?.eSimNetworkMode !== "undefined" ? (this.device.modemSettings?.eSimNetworkMode.toString() === "0" ? "5" : this.device.modemSettings?.eSimNetworkMode.toString()) : null,
        simVoiceDomainPreference: new UntypedFormControl(
          // Sonixx: SIP (VoIP preferred) is a choice of its own and must survive reopening the form;
          // any other value is the one SIM option that goes with the network mode
          this.device.deviceType === '31-15'
            ? (this.device.modemSettings?.simVoiceDomainPreference?.toString() === '4' ? '4' :
              this.device.modemSettings?.simNetworkMode?.toString() === '5' ? '2' :
              this.device.modemSettings?.simNetworkMode?.toString() === '6' ? '3' :
                this.device.modemSettings?.simNetworkMode?.toString() === '1' ? '0' : null)
            : (typeof this.device.modemSettings?.simVoiceDomainPreference !== "undefined" ? this.device.modemSettings?.simVoiceDomainPreference.toString() : null)
        ),
        eSimVoiceDomainPreference: typeof this.device.modemSettings?.eSimVoiceDomainPreference !== "undefined" ? this.device.modemSettings?.eSimVoiceDomainPreference.toString() : null,
        simAllowedOperators: this.device.modemSettings?.simAllowedOperators,
        simBlockedOperators: this.device.modemSettings?.simBlockedOperators,
        eSimAllowedOperators: this.device.modemSettings?.eSimAllowedOperators,
        eSimBlockedOperators: this.device.modemSettings?.eSimBlockedOperators,
        vpnEnabled: this.device.vpnSettings?.enabled,
        vpnServer1: this.device.vpnSettings?.server1,
        vpnServer2: this.device.vpnSettings?.server2,
        vpnDhcp: this.device.vpnSettings?.dhcp,
        vpnIPAddress: this.device.vpnSettings?.ipAddress,
        vpnGateway: this.device.vpnSettings?.gateway,
        vpnSubnetMask: this.device.vpnSettings?.subnetMask
      }),
      bluetoothSettingsFormGroup: this.fb.group({
        bluetoothEnabled: this.device.bluetoothSettings?.enabled
      }),
      sonixxSettingsForm: this.fb.group({
        silentMode: this.device.silentMode,
        // Default to MEDIUM ("1") when the device has no value configured yet
        speakerVolume: this.device.speakerVolume != null ? this.device.speakerVolume.toString() : "1",
        micSensitivity: this.device.micSensitivity != null ? this.device.micSensitivity.toString() : "1",
        alarmToneVolume: this.device.alarmToneVolume != null ? this.device.alarmToneVolume.toString() : "1",
        indicationToneVolume: this.device.indicationToneVolume != null ? this.device.indicationToneVolume.toString() : "1"
      })
    });

    // Remove validators if parameter is not found
    if (!this.device.hasOwnProperty('movementTimeLimit')) {
      this.deviceSettingsForm.controls.devMiscSettingsForm.get('limit').clearValidators();
      this.deviceSettingsForm.controls.devMiscSettingsForm.get('limit').updateValueAndValidity();
    }

    if (!this.device.hasOwnProperty('connectionTimeLimit')) {
      this.deviceSettingsForm.controls.devMiscSettingsForm.get('connTimeLimit').clearValidators();
      this.deviceSettingsForm.controls.devMiscSettingsForm.get('connTimeLimit').updateValueAndValidity();
    }
  }

  getCurrentSimMode(eSim: number, mode: number): string {
    if (mode === 0) {
      if (eSim != null) {
        return "4";
      }
      return "3";
    } else {
      return mode.toString();
    }
  }

  /**
   * Submit device settings form to the server
   * @returns **true** if form is valid, **false** if form is invalid
   */
  submitDeviceSettings(): boolean {
    this.saveError = false;
    this.saveSuccess = false;
    this.saveErrorMessage = '';

    const controls = this.deviceSettingsForm.controls;

    // Submit base additional settings if they were changed
    if (controls.devMiscSettingsForm.dirty)          { this.submitDevMiscSettingsForm(); } 

    // Submit terminal parameters form if they were changed
    if (controls.terminalParameterFormGroup.dirty)   { this.submitTerminalParameters(); } 

    // Submit TP202 additional settings if they were changed
    if (controls.tp202AdditionalForm.dirty)          { this.submitTp202Additional(); } 

    // Submit TP202 network settings if they were changed
    if (controls.devNetworkSettingsForm.dirty)       { this.submitNetworkSettings(); } 

    // Submit TP202G bluetooth settings if they were changed
    if (controls.bluetoothSettingsFormGroup.dirty)   { this.submitBluetoothSettings(); }

    // Submit TP400GW settings if they were changed
    if (controls.sonixxSettingsForm.dirty) { this.submitSonixxSettings(); } 
    // Submit base bitpara settings if they were changed
    else if (controls.devBitParaForm.dirty) { this.submitDeviceBitPara(); }

    return true;
  }

  submitSonixxSettings(): void {
    const form = this.deviceSettingsForm.controls.sonixxSettingsForm.value;
    // Dropdowns always hold a level ("0"/"1"/"2"); guard any unset value so we never post NaN
    const toLevel = (value: any): number | null =>
      value === "" || value === null || value === undefined ? null : Number(value);
    const data = {
      silentMode: form.silentMode,
      speakerVolume: toLevel(form.speakerVolume),
      micSensitivity: toLevel(form.micSensitivity),
      alarmToneVolume: toLevel(form.alarmToneVolume),
      indicationToneVolume: toLevel(form.indicationToneVolume)
    };
    // SaveBitPara transfers its settings via the device, so it must run only after this call succeeds
    this.api.saveSonixxSettings(data, this.device.id).subscribe(res => {
      this.saveSuccess = true;
      this.submitDeviceBitPara();
    }, err => {
      this.saveError = true;
    })
  }

  submitBluetoothSettings(): void {
    const data = {
      deviceId: this.device.id,
      enabled: this.deviceSettingsForm.controls.bluetoothSettingsFormGroup.value.bluetoothEnabled
    }

    this.api.saveBluetoothSettings(data).subscribe(res => {
      this.saveSuccess = true;
    }, err => {
      this.saveError = true;
    })
  }

  submitTerminalParameters(): void {
    const data: any = {};
    data.deviceId = this.device.id;

    data.terminalParameters = {
      lockEscort: this.deviceSettingsForm.controls.terminalParameterFormGroup.value.lockEscort,
      movementMonitoring: this.deviceSettingsForm.controls.terminalParameterFormGroup.value.movementMonitoring,
      fallingDetection: this.deviceSettingsForm.controls.terminalParameterFormGroup.value.fallingDetection,
      accessControl: this.deviceSettingsForm.controls.terminalParameterFormGroup.value.accessControl,
      longPress: this.deviceSettingsForm.controls.terminalParameterFormGroup.value.longPress,
      alwaysRoaming: this.deviceSettingsForm.controls.terminalParameterFormGroup.value.alwaysRoaming
    }

    data.terminalParameters = this.deviceSettingsForm.controls.terminalParameterFormGroup.value;

    //Override other parameters to false if lock escort is enabled
    if (data.terminalParameters.lockEscort) {
      data.terminalParameters.movementMonitoring = false;
      data.terminalParameters.fallingDetection = false;
      data.terminalParameters.accessControl = false;
    }


    this.api.saveAdvancedSettings(data).subscribe(res => {
      this.saveSuccess = true;
    }, err => {
      this.saveError = true;
    })
  }

  submitDevMiscSettingsForm(): void {
    const data: any = {};
    data.deviceId = this.device.id;

    // Parse movement time limit from the form
    const splittedMovementTimeLimit = this.deviceSettingsForm.controls.devMiscSettingsForm.value.limit.split(':')
    const movementTimeLimitSeconds = (Number(splittedMovementTimeLimit[0]) * 60 * 60) + (Number(splittedMovementTimeLimit[1] * 60))
    data.movementTimeLimit = movementTimeLimitSeconds;

    // Parse movement time limit from the form
    const splittedConnLimit = this.deviceSettingsForm.controls.devMiscSettingsForm.value.connTimeLimit.split(':')
    const connTimeLimitSeconds = (Number(splittedConnLimit[0]) * 60 * 60) + (Number(splittedConnLimit[1] * 60))
    data.connectionTimeLimit = connTimeLimitSeconds;

    // Get the activity monitoring value from the form
    data.activityMonitoringEnabled = this.deviceSettingsForm.controls.devMiscSettingsForm.value.activityMonitoring;

    this.api.saveAdvancedSettings(data).subscribe(res => {
      this.saveSuccess = true;
      if (this.deviceSettingsForm.controls.devMiscSettingsForm.value.activityMonitoring != null) {
        this.device.activityMonitoring = this.deviceSettingsForm.controls.devMiscSettingsForm.value.activityMonitoring
      }
      if (data.movementTimeLimitSeconds >= 0) {
        this.device.movementTimeLimit = data.movementTimeLimitSeconds;
      }
      if (data.connTimeLimitSeconds >= 0) {
        this.device.connectionTimeLimit = data.connTimeLimitSeconds;
      }
    }, err => {
      this.saveError = true;
    })
  }

  submitDeviceBitPara(): void {
    // submitSonixxSettings sets saveSuccess before chaining into this method, so the success
    // banner can already be on screen while the VoIP call has not even started. Reset the status
    // here as well as in the top-level submit, so this combined operation reports its own outcome
    // instead of leaving a stale success sitting next to a failure.
    this.saveSuccess = false;
    this.saveError = false;
    this.saveErrorMessage = '';

    let settings: BitParaSettings = new BitParaSettings()
    settings = this.deviceSettingsForm.controls.devBitParaForm.value;

    // Convert automatic clock brightness into ClockAls bit
    if (+ settings.clockBrightness === 4) {
      settings.clockAls = true;
      settings.clockBrightness = 2;
    } else {
      settings.clockAls = false;
    }

    const data: BitParaSettingsRequest = new BitParaSettingsRequest()
    data.deviceId = this.device.id;
    data.deviceType = this.device.deviceType;
    data.settings = settings;
    if (this.device.networkSimSettings) {
      data.networkSimSettings = {};
      data.networkSimSettings.networkMode = this.deviceSettingsForm.controls.devBitParaForm.value.networkMode;
    }

    // VoIP goes through its own endpoint, but its outcome is part of this save. It used to be
    // fired off unwatched, so a provisioning failure was invisible: the bit-para save returned
    // first and reported success while the device had in fact not been provisioned at all.
    forkJoin({
      voip: this.voipChange$(settings),
      bitPara: this.api.saveDeviceBitPara(data).pipe(
        map(res => ({ ok: true, result: res })),
        catchError(() => of({ ok: false, result: null as any }))
      )
    }).subscribe(({ voip, bitPara }) => {
      if (bitPara.ok && bitPara.result) {
        this.device.bitParaSettings = bitPara.result;
      }

      if (bitPara.ok && voip.ok) {
        this.saveSuccess = true;
        return;
      }

      // Either half failing means the save did not fully succeed, so say so rather than
      // reporting success for the part that happened to work.
      this.saveError = true;
      this.saveErrorMessage = voip.message;
    });
  }

  /**
   * Performs the VoIP provisioning change this save implies, if any.
   *
   * Errors are caught rather than propagated so that a VoIP failure cannot abort the rest of the
   * save: the other bit-para settings are independent of it and should still be written.
   *
   * @returns an observable that always emits an outcome, never errors
   */
  private voipChange$(settings: BitParaSettings): Observable<{ ok: boolean, message: string }> {
    // Device had a SIP number and VoIP was switched off: de-provision.
    if (this.device.sipNumber && !settings.voipEnabled) {
      return this.api.deprovisionVoip(this.device.id).pipe(
        map(() => {
          this.device.sipNumber = '';
          return { ok: true, message: '' };
        }),
        catchError(err => {
          // Put the toggle back where the device actually is, so the form stops claiming VoIP
          // is off when the number is still provisioned.
          this.deviceSettingsForm.controls.devBitParaForm.patchValue({ voipEnabled: true });
          return of({ ok: false, message: this.voipErrorMessage(err) });
        })
      );
    }

    // VoIP switched on: provision.
    if (settings.voipEnabled) {
      const voipData = {
        deviceId: this.device.id,
        country: JSON.parse(localStorage.getItem('customerInfo'))?.country
      };

      return this.api.provisionVoip(voipData).pipe(
        map(res => {
          if (res) {
            this.device.sipNumber = res.data.sipNumber;
          }
          return { ok: true, message: '' };
        }),
        catchError(err => {
          // Provisioning failed, so the device has no SIP number. The API rolls its own state
          // back, and the toggle has to follow or the form would show VoIP as enabled.
          this.device.sipNumber = '';
          this.deviceSettingsForm.controls.devBitParaForm.patchValue({ voipEnabled: false });
          return of({ ok: false, message: this.voipErrorMessage(err) });
        })
      );
    }

    // Nothing to do: VoIP was already off, or already on and staying on.
    return of({ ok: true, message: '' });
  }

  /** Pulls the reason out of a failed VoIP call, falling back to nothing rather than a stack trace. */
  private voipErrorMessage(err: any): string {
    return err?.error?.message ?? '';
  }

  submitNetworkSettings(): void {
    const data = this.deviceSettingsForm.controls.devNetworkSettingsForm.value;
    data.deviceId = this.device.id;
    data.ethernetDHCP = data.ethernetDHCP ? 1 : 0;
    data.wifiDHCP = data.wifiDHCP ? 1 : 0;
    data.hotspotEnabled = data.hotspotEnabled ? 1 : 0;
    data.hotspotDHCPServer = data.hotspotDHCPServer ? 1 : 0;
    data.vpnEnabled = data.vpnEnabled ? 1 : 0
    data.vpnDhcp = data.vpnDhcp ? 1 : 0;

    //If hotspot is disabled reset all other values to empty
    if (!data.hotspotEnabled) {
      data.hotspotPassword = '';
      data.hotspotChannel = 6;
      data.hotspotIpAddress = '';
      data.hotspotGateway = '';
      data.hotspotSubnetMask = '';
      data.hotspotDHCPServer = 0;
      data.hotspotDHCPStart = '';
      data.hotspotDHCPEnd = '';
    }

    if (data.hotspotEnabled && !data.hotspotDHCPServer) {
      data.hotspotDHCPStart = '';
      data.hotspotDHCPEnd = '';
    }

    //If VPN is disabled reset all other values to empty
    if (!data.vpnEnabled) {
      data.vpnDhcp = 0;
      data.vpnGateway = '';
      data.vpnIPAddress = '';
      data.vpnSubnetMask = '';
      data.vpnServer1 = '';
      data.vpnServer2 = '';
    }

    this.api.saveDeviceNetworkSettings(data).subscribe(res => {
      this.saveSuccess = true;
    }, err => {
      this.saveError = true;
    })
  }

  submitTp202Additional(): void {
    const data = {
      deviceId: this.device.id,
      ignoreAutoAnswerNumbers: this.deviceSettingsForm.controls.tp202AdditionalForm.value.ignoreAutoAnswer,
      audioLockOpenDtmfString: this.deviceSettingsForm.controls.tp202AdditionalForm.value.dtmfCode,
      delayACPowerAlert: this.deviceSettingsForm.controls.tp202AdditionalForm.value.delayACPowerAlert,
      maxCallDuration: this.deviceSettingsForm.controls.tp202AdditionalForm.value.maxCallDuration,
      maxCallDurationExtended: this.deviceSettingsForm.controls.tp202AdditionalForm.value.maxCallDurationExtended,
      IamOk: []
    }

    //If IamOK feature is disabled set both times to 00:00
    if (!this.deviceSettingsForm.controls.tp202AdditionalForm.value.iAmOkEnabled) {
      data.IamOk[0] = "00:00";
      data.IamOk[1] = "00:00"
    } else {
      data.IamOk[0] = dateToHhmm(this.deviceSettingsForm.controls.tp202AdditionalForm.value.iAmOkStartTime);
      data.IamOk[1] = dateToHhmm(this.deviceSettingsForm.controls.tp202AdditionalForm.value.iAmOkEndTime);
    }

    this.api.saveAdvancedSettings(data).subscribe(res => {
      this.device.ignoreAutoAnswerNumbers = data.ignoreAutoAnswerNumbers;
      this.saveSuccess = true;
    }, err => {
      this.saveError = true;
    })

  }

  showMiscSettings(type: string): boolean {
    if (this.device.deviceType === 'E8-2') {
      if (type === 'all' || type === 'connTimeLim') {
        return true;
      }
      return false;
    }
    switch (type) {
      case "all":
        if (this.device.movementTimeLimit ||
          (this.device.deviceType === '04-1' ||
            this.device.deviceType === '04-2' ||
            this.device.deviceType === '05-3' ||
            this.device.deviceType === '05-4' ||
            this.device.deviceType === '05-5' ||
            this.device.deviceType === '05-7' ||
            this.device.deviceType === '05-8' ||
            this.device.deviceType === '05-9' ||
            this.device.deviceType === '05-10' ||
            this.device.deviceType === '06-2' ||
            this.device.deviceType.substr(0, 2) === '31' ||
            this.device.deviceType.substr(0, 2) === '34' ||
            this.device.deviceType.substr(0, 2) === '89')) {
          return true;
        }
        break;
      case "movement":
        if (this.device.hasOwnProperty('movementTimeLimit')) {
          return true;
        }
        break;
      case "connTimeLim":
        if (this.device.hasOwnProperty('connectionTimeLimit')) {
          return true;
        }
        break;
      case "activityMonitoring":
        if ((this.device.deviceType === '04-2' || this.device.deviceType === '05-4' || this.device.deviceType === '05-5' || this.device.deviceType === '05-7' || this.device.deviceType === '05-8') && this.helper.showIfEnabled("DeviceMovement")) {
          return true;
        }
        break;
      case "network":
        if (this.device.deviceType === '31-13' || this.device.deviceType === '31-14' && this.device.hasOwnProperty('transportOrder') ||
          this.device.deviceType === '31-15') {
          return true;
        }
        break;
      case "tp202_additional":
        if (this.device.deviceType === '31-13' || this.device.deviceType === '31-14' || this.device.deviceType === '31-15') {
          return true;
        }
        break;
      case "termParams":
        if ((this.device.deviceType === '05-7' || this.device.deviceType === "05-8") && this.device.hasOwnProperty('terminalParameters')) {
          return true;
        }
        break;
      case "bluetoothSettings":
        if (this.device.deviceType === '31-14') {
          return true;
        }
        break;
      case "tp202_ac_power_alert_delay":
        if (this.device.softwareVersion >= 48 && this.device.softwareVersion < 4000) {
          return true;
        }
        break;
      case "voip":
        // Minimum supported software version differs between TP-202 and TP-202G
        if (this.device.deviceType === '31-13' && this.device.softwareVersion >= 83) {
          return true;
        }
        if (this.device.deviceType === '31-14' && this.device.softwareVersion >= 84) {
          return true;
        }
        // Sonixx has no software version requirement yet: VoIP support is still being rolled out,
        // so there is no release to gate on. Add one here once it is known.
        if (this.device.deviceType === '31-15') {
          return true;
        }
        break;
      default:
        return false;
    }
    return false;
  }

  showNetworkSettings(type: string): boolean {
    return (
      this.transportPriority1.value === type ||
      this.transportPriority2.value === type ||
      this.transportPriority3.value === type
    );
  }


  /**
   * Whether any panel of the settings accordion renders for this device. When none do, the form has
   * no controls the user can reach, so its save button could never leave the pristine (disabled)
   * state - it is hidden instead of shown permanently greyed out.
   * Locks and beacons (32-*) are the case in practice: their settings live in the separate
   * lock-beacon-settings form below this one, which has its own save button.
   */
  showDeviceSettingsForm(): boolean {
    return !!this.device.bitParaSettings ||
      this.showMiscSettings('network') ||
      this.showMiscSettings('bluetoothSettings') ||
      this.showMiscSettings('termParams') ||
      this.showMiscSettings('all');
  }

  showLockBeaconSettings(): boolean {
    if (this.device.deviceType === "32-3" || this.device.deviceType === "32-8" || this.device.deviceType === '32-1' || this.device.deviceType === '32-6') {
      return true;
    }
    return false;
  }

  // Listen transport priority select changes
  tpPrioOnChange(): void {
    this.transportPriority1.valueChanges.subscribe((value) => {
      this.tpPrioValidity(value, 1);
    })
    this.transportPriority2.valueChanges.subscribe((value) => {
      this.tpPrioValidity(value, 2);
    })
  }

  tpPrioValidity(value: string, priority: number): void {
    switch (priority) {
      case 1:
        // Prevent assigning same value multiple times by resetting values to '3' (-) in other priorities
        if (this.transportPriority2.value === value) this.transportPriority2.setValue('3');
        if (this.transportPriority3.value === value) this.transportPriority3.setValue('3');
        break;
      case 2:
        // Set priority 3 value to NONE, if selected priority 2 is same as priority 3 or NONE
        if (this.transportPriority3.value === value || value === '3') this.transportPriority3.setValue('3');
        break;
      case 3:
        break;
    }
  }

  // Controls behavior of the transport priority form if some or all device setting values are undefined
  isTpPrioUndefined(): void {
    //If priority 1 value is undefined (empty), set all priorities value 3 (-)
    if (this.transportPriority1.value === undefined) {
      this.transportPriority1.setValue('3');
      this.transportPriority2.setValue('3');
      this.transportPriority3.setValue('3');
    }
    if (this.transportPriority2.value === undefined) {
      this.transportPriority2.setValue('3');
      this.transportPriority3.setValue('3');
    }
    if (this.transportPriority3.value === undefined) {
      this.transportPriority3.setValue('3');
    }
  }

  shouldShowField(field: string): boolean {
    if (this.device.deviceType === '31-15') {
      return false;
    }
    return true;
  }

  ngOnInit(): void {
    // Initialize form submit subject with 3 second throttle time to prevent multiple submits
    this.formSubmitSubject.pipe(throttleTime(3000))
      .subscribe(() => {
        this.submitDeviceSettings()
      });
  }

  ngOnChanges() {
    this.user = JSON.parse(localStorage.getItem('user'));
    this.createDeviceSettingsForm();
    this.isTpPrioUndefined();
    this.tpPrioValidity(this.transportPriority1.value, 1);
    this.tpPrioValidity(this.transportPriority2.value, 2);
    this.tpPrioOnChange();
  }

  ngOnDestroy(): void {
    this.formSubmitSubject.unsubscribe();
  }

  get transportPriority1(): UntypedFormControl {
    return this.deviceSettingsForm.controls.devNetworkSettingsForm.get('transportPriority1') as UntypedFormControl;
  }

  get transportPriority2(): UntypedFormControl {
    return this.deviceSettingsForm.controls.devNetworkSettingsForm.get('transportPriority2') as UntypedFormControl;
  }

  get transportPriority3(): UntypedFormControl {
    return this.deviceSettingsForm.controls.devNetworkSettingsForm.get('transportPriority3') as UntypedFormControl;
  }

  get simNetworkMode(): UntypedFormControl {
    return this.deviceSettingsForm.controls.devNetworkSettingsForm.get('simNetworkMode') as UntypedFormControl;
  }

  get eSimNetworkMode(): UntypedFormControl {
    return this.deviceSettingsForm.controls.devNetworkSettingsForm.get('eSimNetworkMode') as UntypedFormControl;
  }

  get simVoiceDomainPreference(): UntypedFormControl {
    return this.deviceSettingsForm.controls.devNetworkSettingsForm.get('simVoiceDomainPreference') as UntypedFormControl;
  }

  get eSimVoiceDomainPreference(): UntypedFormControl {
    return this.deviceSettingsForm.controls.devNetworkSettingsForm.get('eSimVoiceDomainPreference') as UntypedFormControl;
  }

  simNetworkModeChange(event: any): void {
    const type = this.device.deviceType;

    // VoIP preferred is selectable on every network mode, so keep it as is
    if (this.simVoiceDomainPreference.value === '4') return;

    if (type === '31-15') {
      switch (event.value) {
        case '5':
          this.simVoiceDomainPreference.setValue('2');
          break;
        case '6':
          this.simVoiceDomainPreference.setValue('3');
          break;
        case '1':
          this.simVoiceDomainPreference.setValue('0');
          break;
      }
    } else if (type === '31-14') {
      switch (event.value) {
        case '5': this.simVoiceDomainPreference.setValue('2');
          break;
        case '6': this.simVoiceDomainPreference.setValue('3');
          break;
        case '1': this.simVoiceDomainPreference.setValue('0');
          break;
      }
    }
  }

  simVoiceDomainPreferenceChange(event: any): void {
    // Sonixx cannot make SIP calls over 2G, so choosing SIP while on 2G only falls back to Auto
    if (this.device.deviceType === '31-15' && event.value === '4' && this.simNetworkMode.value === '1') {
      this.simNetworkMode.setValue('5');
    }
  }

  eSimNetworkModeChange(event: any): void {
    // VoIP preferred is selectable on every network mode, so keep it as is
    if (this.eSimVoiceDomainPreference.value === '4') return;

    if (event.value === "5" && this.device.deviceType === "31-14") {
      this.eSimVoiceDomainPreference.setValue("2");
    }

    if (event.value === "6" && this.device.deviceType === "31-14") {
      this.eSimVoiceDomainPreference.setValue("3");
    }

    if (event.value === "1" && this.device.deviceType === "31-14") {
      this.eSimVoiceDomainPreference.setValue("0");
    }
  }
}

export class BitParaSettingsRequest {
  deviceId: string;
  deviceType: string;
  settings: BitParaSettings;
  networkSimSettings: any;
}
